import CustomButton from 'components/CustomButton'
import { OutlinedButton } from 'components/CustomButton'
import { DangerButton } from 'components/CustomButton'
import CustomModal from 'components/CustomModal'
import Input from 'components/form/Input'
import { isoFormatToLocalTimeString } from 'helpers/dateTimeHelper'
import { isoToLocaleFormat } from 'helpers/dateTimeHelper'
import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { debounce, isEmpty, isEqual } from 'lodash'
import {
  getViberPrescription,
  updateName,
} from 'redux/slices/prescriptionSlice'
import { createOrder } from 'redux/slices/prescriptionSlice'
import Loader from 'components/Loader'
import { getScannedPrescriptionOrder } from 'redux/slices/prescriptionSlice'
import { tabItems } from './PrescriptionList'
import NavbarInput from 'components/form/NavbarInput'
import LabeledSelect from 'components/selects/LabeledSelect'
import TableItemLoader from 'components/TableItemLoader'
import { SearchType } from './constants'
import CancelOrder from './CancelOrder'
import { cancelOrder } from 'redux/slices/prescriptionSlice'
import Viewer from 'react-viewer'
import Pagination from 'components/Pagination'

const PrescriptionOrder = ({ activeTab, updateTab, page = 1 }) => {
  const dispatch = useDispatch()
  const [activeModalComponent, setActiveModalComponent] = useState(null)
  const [modalData, setModalData] = useState(undefined)
  const navigate = useNavigate()
  const [selectedOrder, setSelectedOrder] = useState({})
  const [searchType, setSelectedSearchType] = useState(undefined)
  const [userSearch, setUserSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [imagePreview, setImagePreview] = useState({
    visible: false,
    image: {},
  })
  const [queryParams, setQueryParams] = useState({
    limit: 10,
    offset: page,
  })

  const {
    prescriptions,
    totalResult,
    processing,
    loader,
    loading,
    error,
    totalPages,
  } = useSelector((state) => state.prescription)

  useEffect(() => {
    if (activeTab !== undefined) {
      if (activeTab === tabItems[0]) {
        getPrescriptionOrder.viber()
      } else {
        getPrescriptionOrder.scanned()
      }
    }
  }, [activeTab])

  useEffect(() => {
    if (activeTab !== undefined) {
      if (tabItems[0] === activeTab) {
        getPrescriptionOrder.viber()
      } else {
        getPrescriptionOrder.scanned()
      }
    }
  }, [queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, offset: page })
  }, [page])

  useEffect(() => {
    setModalData(undefined)
    setActiveModalComponent(null)
    setSelectedOrder({})
  }, [prescriptions])

  const getPrescriptionOrder = {
    viber: () => dispatch(getViberPrescription(page)),
    scanned: () =>
      dispatch(
        getScannedPrescriptionOrder({
          ...queryParams,
          status: activeTab.status,
        }),
      ),
  }

  const debouncedInput = useCallback(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    debounce((e) => {
      if(/[a-z0-9]/i.test(e.target.value || e.target.value.length === 0)){
        setQueryParams({ ...queryParams, [searchType.value]: e.target.value})
      }
    }, 500),
    [searchType],
  )

  const ModalOptions = {
    updateName: {
      title: 'Update Name',
      child: ({ modalData }) => (
        <div>
          <Input
            type="First Name*"
            color="blue"
            placeholder="First Name"
            value={modalData?.first_name ?? ''}
            onChange={(e) => {
              setModalData({ ...modalData, first_name: e.target.value.trim() })
            }}
          />
          <Input
            type="Last Name*"
            color="blue"
            placeholder="Last Name"
            value={modalData?.last_name ?? ''}
            onChange={(e) => {
              setModalData({ ...modalData, last_name: e.target.value.trim() })
            }}
          />
        </div>
      ),
      onComplete: ({ processing, modalData, selectedOrder }) => (
        <CustomButton
          disabled={processing || !ModalOptions.updateName.validate(modalData)}
          onClick={() => {
            dispatch(updateName({ id: selectedOrder.id, payload: modalData }))
          }}
        >
          Ok
        </CustomButton>
      ),
      validate: (modalData) => {
        return !isEmpty(modalData?.first_name) && !isEmpty(modalData?.last_name)
      },
    },
    createOrder: {
      title: 'Create Order',
      child: () => <div>Do you want to create order?</div>,
      onComplete: ({ processing, selectedOrder }) => (
        <CustomButton
          disabled={processing}
          onClick={() => {
            dispatch(createOrder({ bot_message_id: selectedOrder.id }))
          }}
        >
          Ok
        </CustomButton>
      ),
    },
    cancelOrder: {
      title: 'Cancel Order',
      child: ({ modalData }) => (
        <CancelOrder modalData={modalData} setModalData={setModalData} />
      ),
      onComplete: ({ processing, modalData, selectedOrder }) => (
        <CustomButton
          disabled={processing || !ModalOptions.cancelOrder.validate(modalData)}
          onClick={() => {
            dispatch(
              cancelOrder({
                prescriptionId: selectedOrder.id,
                payload: modalData,
              }),
            )
          }}
        >
          Ok
        </CustomButton>
      ),
      validate: (modalData) => {
        return !isEmpty(modalData?.reason)
      },
    },
  }

  const formatDate = (date, type) => {
    return `${date} 00:01:00 GMT`
  }

  const handlePageClick = (data) => {
    let currentPage
    data.selected < 0 ? (currentPage = 1) : (currentPage = data.selected + 1)
    updateTab(activeTab, currentPage)
  }

  const getDate = (prescription) => {
    return prescription?.processing_date
      ? prescription?.processing_date
      : prescription?.requested_date
      ? prescription?.requested_date
      : prescription?.confirmed_date
  }

  return (
    <div>
      <div className="flex justify-between">
        <div>
          <div className="text-lg font-bold">{activeTab?.header}</div>
          <div>{`You have ${loading ? "0" : totalResult} new orders.`}</div>
        </div>
        <div className="w-20" />
        {activeTab !== tabItems[0] && (
          <div className="flex flex-wrap justify-end flex-1 space-x-5">
            <div className="flex space-x-5">
              <div className="w-60">
                <Input
                  type="date"
                  color="blue"
                  placeholder="Start Date"
                  value={startDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      setQueryParams({
                        ...queryParams,
                        start_date: formatDate(e.target.value, 'start'),
                      })
                    } else {
                      let param = { ...queryParams }
                      delete param['start_date']
                      setQueryParams(param)
                    }
                    setStartDate(e.target.value)
                    updateTab(activeTab, 1)
                  }}
                />
              </div>
              <div className="w-60">
                <Input
                  type="date"
                  color="blue"
                  placeholder="End Date"
                  value={endDate}
                  onChange={(e) => {
                    if (e.target.value) {
                      setQueryParams({
                        ...queryParams,
                        end_date: formatDate(e.target.value, 'end'),
                      })
                    } else {
                      let param = { ...queryParams }
                      delete param['end_date']
                      setQueryParams(param)
                    }
                    setEndDate(e.target.value)
                    updateTab(activeTab, 1)
                  }}
                />
              </div>
            </div>
            <div className="mt-2 w-60">
              <LabeledSelect
                placeholder="Select Search Type..."
                onChange={(type) => {
                  setSelectedSearchType(type)
                  setUserSearch('')
                }}
                options={SearchType}
                getOptionValue={(option) => option?.value}
                getOptionLabel={(option) => option?.label}
                value={searchType}
              />
            </div>
            <div className="h-10 mt-2 border border-gray-300 rounded w-60">
              <NavbarInput
                placeholder="Search Order By ..."
                disabled={!searchType}
                onChange={(e) => {
                  debouncedInput(e)
                  setUserSearch(e.target.value)
                  updateTab(activeTab, 1)
                }}
                value={userSearch}
              />
            </div>
          </div>
        )}
      </div>
      <hr />
      {loading || !isEmpty(error) || isEmpty(prescriptions) ? (
        <TableItemLoader
          loading={loading}
          error={error}
          isEmpty={isEmpty(prescriptions)}
          retry={() => {}}
          count={10}
          key={activeTab?.prefix}
        />
      ) : (
        <>
          <ul>
            {prescriptions.map((prescription, index) => (
              <li
                key={`${activeTab?.prefix}-${index}`}
                className="flex justify-between p-3 my-5 bg-gray-100 border rounded-md"
              >
                {isEqual(activeTab, tabItems[0]) ? (
                  <div className="flex flex-col">
                    <div>{`${prescription.first_name} ${prescription.requester?.middle_name ? prescription.requester?.middle_name : ''} ${prescription.last_name}(${prescription.mobile_number}) added prescription from viber`}</div>
                    <div className="text-sm font-bold">
                      Created At:
                      {isoToLocaleFormat(prescription.created_at, true)},
                      {isoFormatToLocalTimeString(
                        prescription.created_at,
                        true,
                      )}
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex flex-col cursor-pointer"
                    onClick={() => {
                      navigate(
                        `/orders/${prescription.id}?status=${activeTab.status}`,
                        { replace: false },
                      )
                    }}
                  >
                    <div>{`${prescription.requester?.first_name} ${prescription.requester?.middle_name ? prescription.requester?.middle_name : ''} ${prescription.requester?.last_name} (${prescription.requester?.mobile}) has created an order for ${prescription?.requested_for?.first_name} ${prescription.requester?.middle_name ? prescription.requester?.middle_name : ''}  ${prescription?.requested_for?.last_name} (${prescription?.requested_for?.mobile}).  `}</div>
                    <div className="flex flex-wrap text-sm font-bold">
                      <div className="text-sm font-bold">
                        Created At:
                        {' '}
                        {isoToLocaleFormat(getDate(prescription), true)},
                        {isoFormatToLocalTimeString(
                          getDate(prescription),
                          true,
                        )}
                      </div>
                      <div className="w-5" />
                      <div>Order Id: {prescription.id}</div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end items-center space-x-4">
                  {activeTab === tabItems[0] && (
                    <>
                      <OutlinedButton
                        onClick={() => {
                          setImagePreview({
                            visible: true,
                            image: { src: prescription.image_url },
                          })
                        }}
                      >
                        View Image
                      </OutlinedButton>
                      <CustomButton
                        onClick={() => {
                          setModalData({
                            first_name: prescription.first_name,
                            last_name: prescription.last_name,
                          })
                          setSelectedOrder(prescription)
                          setActiveModalComponent(ModalOptions.updateName)
                        }}
                      >
                        Update Name
                      </CustomButton>
                      <DangerButton
                        onClick={() => {
                          setModalData({})
                          setSelectedOrder(prescription)
                          setActiveModalComponent(ModalOptions.createOrder)
                        }}
                      >
                        Create Order
                      </DangerButton>
                    </>
                  )}
                  {activeTab !== tabItems[0] && (
                    <DangerButton
                      onClick={() => {
                        setModalData({})
                        setSelectedOrder(prescription)
                        setActiveModalComponent(ModalOptions.cancelOrder)
                      }}
                    >
                      Cancel Order
                    </DangerButton>
                  )}
                </div>
              </li>
            ))}
          </ul>
          <Pagination 
            pageCount={totalPages}
            onPageChange={handlePageClick}
            initialPage={page - 1}
            forcePage={page - 1}
          />
        </>
      )}

      <CustomModal
        showModal={modalData}
        loading={processing}
        setShowModal={() => {
          setModalData(undefined)
          setSelectedOrder({})
          setActiveModalComponent(null)
        }}
        title={activeModalComponent?.title}
        children={activeModalComponent?.child({ modalData, selectedOrder })}
        button={activeModalComponent?.onComplete({
          modalData,
          processing,
          selectedOrder,
        })}
      />
      {loader && <Loader />}
      <Viewer
        visible={imagePreview.visible}
        onClose={() => {
          setImagePreview({ visible: false, image: {} })
        }}
        images={[imagePreview?.image]}
      />
    </div>
  )
}

export default PrescriptionOrder
